import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { API } from '../utils/api';

const UserProfilePage = ({ showToast, userId, onBack }) => {
  const { user, token } = useAuth();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [bio, setBio] = useState('');
  const [interests, setInterests] = useState('');
  const [callsign, setCallsign] = useState('');
  const [saving, setSaving] = useState(false);

  const targetUserId = userId || (user ? String(user.id || user._id || '') : '');
  const isOwnProfile = !userId || targetUserId === String(user?.id || user?._id || '');

  useEffect(() => {
    // For own profile, use data from auth context (no XHR needed)
    if (isOwnProfile && user) {
      setProfile({
        user_id: targetUserId,
        username: user.username || '',
        bio: user.bio || '',
        interests: user.interests || [],
        callsign: user.callsign || '',
        avatar_url: user.profile_image_url || '',
        joined: user.joined || null,
        stats: user.stats || {}
      });
      setBio(user.bio || '');
      setInterests((user.interests || []).join(', '));
      setCallsign(user.callsign || user.username || '');
      setLoading(false);
      return;
    }
    
    // For other users, fetch via the statistics router (avoids platform interception)
    if (targetUserId) {
      setLoading(true);
      const timer = setTimeout(() => {
        const x = new XMLHttpRequest();
        x.open('GET', `${API}/user-stats?uid=${targetUserId}`, true);
        if (token) x.setRequestHeader('Authorization', `Bearer ${token}`);
        x.onload = function() {
          if (x.status === 200) {
            try {
              const data = JSON.parse(x.responseText);
              setProfile(data);
              setBio(data.bio || '');
              setInterests((data.interests || []).join(', '));
              setCallsign(data.callsign || '');
            } catch(e) {}
          }
          setLoading(false);
        };
        x.onerror = function() { setLoading(false); };
        x.send();
      }, 1000);
      const safetyTimer = setTimeout(() => setLoading(false), 10000);
      return () => { clearTimeout(timer); clearTimeout(safetyTimer); };
    } else {
      setLoading(false);
    }
  }, [targetUserId, token, isOwnProfile, user]);

  const handleSave = () => {
    if (!token) return;
    setSaving(true);
    setTimeout(() => {
      const x = new XMLHttpRequest();
      x.open('PUT', `${API}/profile`, true);
      x.setRequestHeader('Content-Type', 'application/json');
      x.setRequestHeader('Authorization', `Bearer ${token}`);
      x.onreadystatechange = function () {
        if (x.readyState === 4) {
          if (x.status === 200) {
            showToast && showToast('Profile updated!', 'success');
            setEditing(false);
            // Update profile locally without re-fetching
            setProfile(prev => ({
              ...prev,
              bio: bio.trim(),
              interests: interests.split(',').map(s => s.trim()).filter(Boolean),
              callsign: callsign.trim()
            }));
          } else {
            showToast && showToast('Failed to update profile', 'error');
          }
          setSaving(false);
        }
      };
      x.send(JSON.stringify({
        bio: bio.trim(),
        interests: interests.split(',').map(s => s.trim()).filter(Boolean),
        callsign: callsign.trim()
      }));
    }, 200);
  };

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: 60 }} data-testid="profile-loading">
        <div style={{ fontSize: '2.5rem', marginBottom: 15, animation: 'pulse 1.5s ease-in-out infinite' }}>&#128100;</div>
        <p style={{ color: '#a1a1aa', fontSize: '0.95rem' }}>Loading profile...</p>
      </div>
    );
  }

  if (!profile) {
    return (
      <div style={{ textAlign: 'center', padding: 60 }}>
        <p style={{ color: '#f87171' }}>Profile not found</p>
        {onBack && <button onClick={onBack} style={{ marginTop: 15, color: '#047857', background: 'none', border: 'none', cursor: 'pointer' }}>Go Back</button>}
      </div>
    );
  }

  const stats = profile.stats || {};

  return (
    <div style={{ maxWidth: 900, margin: '0 auto', padding: '20px 15px' }} data-testid="user-profile-page">
      {/* Branding */}
      <div style={{ textAlign: 'center', marginBottom: 8, fontSize: '0.7rem', color: '#71717a', letterSpacing: 1 }}>
        Advanced Research Platform &middot; Information Exchange Network
      </div>

      {onBack && (
        <button onClick={onBack} data-testid="profile-back-btn"
          style={{ background: 'rgba(253, 224, 71,0.15)', border: '1px solid rgba(253, 224, 71,0.3)', color: '#c4b5fd', padding: '6px 14px', borderRadius: 8, cursor: 'pointer', marginBottom: 15, fontSize: '0.8rem' }}>
          &larr; Back
        </button>
      )}

      {/* Profile Header */}
      <div style={{
        background: 'linear-gradient(135deg, rgba(253, 224, 71,0.15), rgba(245, 158, 11,0.1))',
        borderRadius: 16, padding: 30, border: '1px solid rgba(253, 224, 71,0.25)', marginBottom: 20
      }} data-testid="profile-header">
        <div style={{ display: 'flex', alignItems: 'center', gap: 20, flexWrap: 'wrap' }}>
          <div style={{
            width: 80, height: 80, borderRadius: '50%', background: 'linear-gradient(135deg, #FDE047, #FDE68A)',
            display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '2rem', color: '#0F172A', fontWeight: 700,
            border: '3px solid rgba(253, 224, 71,0.5)', overflow: 'hidden'
          }}>
            {profile.avatar_url
              ? <img src={profile.avatar_url} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
              : (profile.username || '?')[0].toUpperCase()
            }
          </div>
          <div style={{ flex: 1 }}>
            <h1 style={{ color: '#0F172A', fontSize: '1.6rem', margin: 0, fontWeight: 700 }} data-testid="profile-username">
              {profile.username}
              {profile.callsign && <span style={{ color: '#047857', fontSize: '0.85rem', marginLeft: 10, fontWeight: 400 }}>"{profile.callsign}"</span>}
            </h1>
            {profile.joined && (
              <p style={{ color: '#71717a', fontSize: '0.75rem', margin: '4px 0 0' }}>
                Joined {new Date(profile.joined).toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
              </p>
            )}
          </div>
          {isOwnProfile && !editing && (
            <button onClick={() => setEditing(true)} data-testid="edit-profile-btn"
              style={{ background: 'rgba(253, 224, 71,0.2)', border: '1px solid rgba(253, 224, 71,0.4)', color: '#c4b5fd', padding: '8px 18px', borderRadius: 8, cursor: 'pointer', fontSize: '0.85rem' }}>
              Edit Profile
            </button>
          )}
        </div>

        {/* Bio */}
        <div style={{ marginTop: 15 }}>
          {editing ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              <label style={{ color: '#a1a1aa', fontSize: '0.75rem' }}>Bio (max 500 chars)</label>
              <textarea value={bio} onChange={e => setBio(e.target.value)} maxLength={800} rows={4} data-testid="profile-bio-input"
                style={{ background: '#334155', border: '1px solid rgba(253, 224, 71,0.3)', color: '#334155', padding: 10, borderRadius: 8, resize: 'vertical', fontSize: '0.85rem' }} />
              <div style={{ color: '#71717a', fontSize: '0.65rem', textAlign: 'right' }}>{bio.length}/800</div>
              <label style={{ color: '#a1a1aa', fontSize: '0.75rem' }}>Callsign / Nickname</label>
              <input value={callsign} onChange={e => setCallsign(e.target.value)} maxLength={50} data-testid="profile-callsign-input"
                style={{ background: '#334155', border: '1px solid rgba(253, 224, 71,0.3)', color: '#334155', padding: 8, borderRadius: 8, fontSize: '0.85rem' }} />
              <label style={{ color: '#a1a1aa', fontSize: '0.75rem' }}>Interests (comma-separated)</label>
              <input value={interests} onChange={e => setInterests(e.target.value)} data-testid="profile-interests-input"
                placeholder="e.g. Science, Engineering, Cooking"
                style={{ background: '#334155', border: '1px solid rgba(253, 224, 71,0.3)', color: '#334155', padding: 8, borderRadius: 8, fontSize: '0.85rem' }} />
              <div style={{ display: 'flex', gap: 10 }}>
                <button onClick={handleSave} disabled={saving} data-testid="save-profile-btn"
                  style={{ background: '#FDE047', color: '#0F172A', border: 'none', padding: '8px 20px', borderRadius: 8, cursor: saving ? 'wait' : 'pointer', fontSize: '0.85rem' }}>
                  {saving ? 'Saving...' : 'Save'}
                </button>
                <button onClick={() => setEditing(false)} data-testid="cancel-profile-btn"
                  style={{ background: 'rgba(239,68,68,0.15)', color: '#f87171', border: '1px solid rgba(239,68,68,0.3)', padding: '8px 20px', borderRadius: 8, cursor: 'pointer', fontSize: '0.85rem' }}>
                  Cancel
                </button>
              </div>
            </div>
          ) : (
            <>
              <p style={{ color: '#d1d5db', fontSize: '0.9rem', lineHeight: 1.6, margin: '8px 0' }} data-testid="profile-bio-text">
                {profile.bio || (isOwnProfile ? 'Click "Edit Profile" to add a bio' : 'No bio yet')}
              </p>
              {(profile.interests || []).length > 0 && (
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 10 }} data-testid="profile-interests">
                  {profile.interests.map((interest, i) => (
                    <span key={i} style={{
                      background: 'rgba(253, 224, 71,0.2)', border: '1px solid rgba(253, 224, 71,0.3)',
                      color: '#c4b5fd', padding: '4px 10px', borderRadius: 12, fontSize: '0.75rem'
                    }}>{interest}</span>
                  ))}
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {/* Statistics Grid */}
      <h2 style={{ color: '#334155', fontSize: '1.1rem', marginBottom: 12, fontWeight: 600 }}>Activity Statistics</h2>
      <div style={{
        display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))', gap: 12, marginBottom: 20
      }} data-testid="profile-stats-grid">
        {[
          { label: 'URLs Submitted', value: stats.urls_submitted || 0, color: '#FDE047', icon: '&#128279;' },
          { label: 'Categories Created', value: stats.categories_created || 0, color: '#3b82f6', icon: '&#128193;' },
          { label: 'Searches Performed', value: stats.searches_performed || 0, color: '#10b981', icon: '&#128269;' },
          { label: 'Protocols Created', value: stats.protocols_created || 0, color: '#f59e0b', icon: '&#128220;' },
          { label: 'Reactions Given', value: stats.reactions_given || 0, color: '#047857', icon: '&#10084;' },
          { label: 'Messages Sent', value: stats.messages_sent || 0, color: '#06b6d4', icon: '&#128172;' },
          { label: 'Rooms Joined', value: stats.rooms_joined || 0, color: '#F59E0B', icon: '&#127970;' },
        ].map((stat, i) => (
          <div key={i} style={{
            background: `${stat.color}10`, border: `1px solid ${stat.color}30`,
            borderRadius: 12, padding: 16, textAlign: 'center', transition: 'transform 0.2s'
          }} data-testid={`stat-${stat.label.toLowerCase().replace(/\s+/g, '-')}`}>
            <div style={{ fontSize: '1.5rem', marginBottom: 6 }} dangerouslySetInnerHTML={{ __html: stat.icon }} />
            <div style={{ fontSize: '1.4rem', fontWeight: 700, color: stat.color }}>{stat.value}</div>
            <div style={{ fontSize: '0.7rem', color: '#a1a1aa', marginTop: 4 }}>{stat.label}</div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default UserProfilePage;
